import React from 'react';
import { motion } from 'framer-motion';
import { SectionHeader } from '../components/SectionHeader';
import { Button } from '../components/Button';
import { GlowOrb } from '../components/GlowOrb';
import { Moon, Sparkles, Heart, Award } from 'lucide-react';
export function About() {
  return <div className="pb-24 overflow-hidden" data-id="element-212">
      {/* Hero */}
      <section className="pt-32 pb-32 relative min-h-[80vh] flex items-center overflow-hidden" data-id="element-213">
        <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{
        backgroundImage: `url(https://images.unsplash.com/photo-1518709268805-4e9042af2176?q=80&w=2000&auto=format&fit=crop)`
      }} data-id="element-214"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-midnight via-midnight/70 to-midnight" data-id="element-215"></div>
        <GlowOrb color="gold" size="xl" position="top-0 left-1/2 -translate-x-1/2" data-id="element-216" />
        <div className="container mx-auto px-6 relative z-10" data-id="element-217">
          <div className="max-w-4xl mx-auto text-center" data-id="element-218">
            <SectionHeader eyebrow="Welcome to Calmoras" title="A Sacred Space for Deep Healing" description="Calmoras is a sacred healing space created to guide individuals toward emotional freedom, spiritual alignment, and energetic transformation." data-id="element-219" />
          </div>
        </div>
      </section>

      {/* Story Section - Inverted Theme */}
      <section className="bg-[#F2E8D5] text-midnight py-32 relative" data-id="element-220">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-multiply" data-id="element-221"></div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-222">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center max-w-7xl mx-auto" data-id="element-223">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative" data-id="element-224">
              <div className="aspect-[4/5] rounded-[3rem] overflow-hidden shadow-2xl relative" data-id="element-225">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1000&auto=format&fit=crop')] bg-cover bg-center" data-id="element-226"></div>
              </div>
              <div className="absolute -bottom-10 -right-10 bg-white p-8 rounded-[2rem] shadow-xl border border-midnight/5" data-id="element-227">
                <p className="font-serif text-3xl text-midnight mb-1" data-id="element-228">
                  Aurora Bellweather
                </p>
                <p className="text-gold text-xs font-bold uppercase tracking-widest" data-id="element-229">
                  Founder & Guide
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="space-y-8" data-id="element-230">
              <h2 className="text-5xl font-serif text-midnight" data-id="element-231">
                My Journey to the Light
              </h2>
              <div className="space-y-6 text-midnight/70 text-lg leading-relaxed" data-id="element-232">
                <p data-id="element-233">
                  My path to energy healing wasn't born from curiosity, but from
                  necessity. Over a decade ago, I found myself completely
                  disconnected—physically exhausted, emotionally numb, and
                  spiritually adrift.
                </p>
                <p data-id="element-234">
                  It was through my own dark night of the soul that I discovered
                  the profound power of energetic alignment. As I healed my own
                  chakras and learned to read the cosmic map of my birth chart,
                  my entire reality shifted.
                </p>
                <p data-id="element-235">
                  I created Calmoras Healing Sanctuary to be the safe harbor I
                  once needed. A place where ancient wisdom meets modern
                  understanding, free from judgment and full of unconditional
                  support.
                </p>
              </div>

              <div className="pt-8 border-t border-midnight/10" data-id="element-236">
                <h3 className="font-serif text-2xl mb-6 text-midnight" data-id="element-237">
                  Certifications & Lineage
                </h3>
                <ul className="space-y-4" data-id="element-238">
                  <li className="flex items-center gap-4 text-midnight/80 font-medium" data-id="element-239">
                    <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center" data-id="element-240">
                      <Award className="w-5 h-5 text-gold-dark" data-id="element-241" />
                    </div>
                    Certified Master Usui Reiki Practitioner
                  </li>
                  <li className="flex items-center gap-4 text-midnight/80 font-medium" data-id="element-242">
                    <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center" data-id="element-243">
                      <Moon className="w-5 h-5 text-gold-dark" data-id="element-244" />
                    </div>
                    Professional Evolutionary Astrologer
                  </li>
                  <li className="flex items-center gap-4 text-midnight/80 font-medium" data-id="element-245">
                    <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center" data-id="element-246">
                      <Heart className="w-5 h-5 text-gold-dark" data-id="element-247" />
                    </div>
                    Trauma-Informed Somatic Coach
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-32 relative overflow-hidden" data-id="element-248">
        <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{
        backgroundImage: `url(https://images.unsplash.com/photo-1518495973542-4542c06a5843?q=80&w=2000&auto=format&fit=crop)`
      }} data-id="element-249"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-midnight via-midnight/80 to-midnight" data-id="element-250"></div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-251">
          <div className="glass rounded-[3rem] p-12 md:p-24 text-center relative overflow-hidden max-w-5xl mx-auto" data-id="element-252">
            <GlowOrb color="primary" size="lg" position="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-253" />
            <div className="relative z-10" data-id="element-254">
              <Sparkles className="w-12 h-12 text-gold mx-auto mb-8" data-id="element-255" />
              <h2 className="text-4xl md:text-5xl font-serif text-white mb-8" data-id="element-256">
                The Calmoras Philosophy
              </h2>
              <p className="text-xl md:text-2xl text-beige-muted leading-relaxed mb-12 font-serif italic" data-id="element-257">
                "We believe that true healing comes from within. Our role is not
                to 'fix' you, but to help clear the energetic blockages that
                prevent you from accessing your own innate healing power and
                divine wisdom."
              </p>
              <Button href="/services" variant="primary" size="lg" data-id="element-258">
                Explore Our Methods
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>;
}